import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Field, FormProgress, FormSection, Tile, areaCls, inputCls } from "@/components/forms/FormKit";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, BadgeCheck, Building2, CalendarDays, CheckCircle2, GraduationCap, Lightbulb, Mail, MapPin, MessageSquare, Mic2, Phone, UserRound, Users, Wallet } from "lucide-react";

const ORG_TYPES = [
  { v: "college", l: "College / University", Icon: GraduationCap },
  { v: "company", l: "Company / Workplace", Icon: Building2 },
  { v: "other", l: "Other", Icon: Users },
];
const EMPTY = { org_type: "college", org_name: "", contact_name: "", designation: "", email: "", phone: "", location: "", preferred_dates: "", audience_size: "", budget: "", topic: "", message: "" };
const YOU = [
  { k: "contact_name", l: "Your name", req: true, Icon: UserRound, ph: "Full name" },
  { k: "designation", l: "Your designation", Icon: BadgeCheck, ph: "e.g. Placement Officer, HR Lead" },
  { k: "email", l: "Email", req: true, type: "email", Icon: Mail, ph: "you@organisation.com" },
  { k: "phone", l: "Phone / WhatsApp", req: true, type: "tel", Icon: Phone, ph: "+91 98xxxxxx10" },
];
const EVENT = [
  { k: "org_name", l: "Organisation name", req: true, Icon: Building2, ph: "e.g. ABC Institute of Technology", wide: true },
  { k: "location", l: "City & country", req: true, Icon: MapPin, ph: "e.g. Bengaluru, India" },
  { k: "preferred_dates", l: "Preferred date(s)", Icon: CalendarDays, ph: "e.g. Any Friday in March" },
  { k: "audience_size", l: "Expected audience", Icon: Users, ph: "e.g. 200 students" },
  { k: "topic", l: "Topic you have in mind", Icon: Lightbulb, ph: "e.g. Careers in data & AI" },
  { k: "budget", l: "Budget for the event", req: true, Icon: Wallet, ph: "Mention if travel & stay are covered", wide: true },
];
const filled = (v) => String(v || "").trim().length > 0;
const emailOk = (v) => /\S+@\S+\.\S+/.test(v);

function Fields({ list, form, set }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {list.map((f) => (
        <Field key={f.k} label={f.l} htmlFor={`inv-${f.k}`} required={f.req} icon={f.Icon} accent="violet" filled={f.type === "email" ? emailOk(form[f.k]) : filled(form[f.k])} className={f.wide ? "sm:col-span-2" : ""}>
          <input id={`inv-${f.k}`} data-testid={`invite-${f.k}`} type={f.type || "text"} required={f.req} value={form[f.k]} onChange={set(f.k)} placeholder={f.ph} className={inputCls} />
        </Field>
      ))}
    </div>
  );
}

export default function InviteSankalp() {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const done = [true, filled(form.contact_name) && emailOk(form.email) && filled(form.phone), filled(form.org_name) && filled(form.location) && filled(form.budget)];

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/speaker-invites", form);
      setSent(true);
      toast.success("Invite sent to Sankalp's team");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <Navbar variant="adult" />
      <section className="relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[40rem] h-[40rem] rounded-full bg-violet-500/10 blur-3xl pointer-events-none" />
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 pt-10 pb-24 relative">
          <Link to="/meet-sankalp" data-testid="invite-back" className="group inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-violet-300 transition-colors">
            <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" /> Meet Sankalp
          </Link>
          <div className="mt-8 grid lg:grid-cols-12 gap-10 items-start">
            <InviteIntro />
            <div className="lg:col-span-7">
              {sent ? <InviteSent email={form.email} /> : (
                <form onSubmit={submit} data-testid="invite-form" className="relative rounded-3xl border border-zinc-800 bg-zinc-950/90 backdrop-blur-xl p-5 sm:p-8 shadow-[0_30px_80px_-40px_rgba(139,92,246,0.6)]">
                  <div aria-hidden className="absolute -top-px left-10 right-10 h-px bg-gradient-to-r from-transparent via-violet-400/80 to-transparent" />
                  <FormProgress done={done.filter(Boolean).length} total={3} accent="violet" />
                  <FormSection n="01" title="Who's inviting?" accent="violet" done testid="invite-section-type">
                    <div className="grid grid-cols-3 gap-2">
                      {ORG_TYPES.map(({ v, l, Icon }) => (
                        <Tile key={v} testid={`invite-type-${v}`} accent="violet" on={form.org_type === v} onClick={() => setForm({ ...form, org_type: v })} icon={Icon} title={l} />
                      ))}
                    </div>
                  </FormSection>
                  <FormSection n="02" title="Your details" hint="So the team knows who to get back to." accent="violet" done={done[1]} testid="invite-section-you">
                    <Fields list={YOU} form={form} set={set} />
                  </FormSection>
                  <FormSection n="03" title="About the event" hint="The more we know, the better the session." accent="violet" done={done[2]} testid="invite-section-event">
                    <Fields list={EVENT} form={form} set={set} />
                  </FormSection>
                  <FormSection n="04" title="Anything else?" hint="Optional" accent="violet" done={filled(form.message)} last testid="invite-section-more">
                    <Field label="Notes for Sankalp's team" htmlFor="inv-message" icon={MessageSquare} top accent="violet" filled={filled(form.message)}>
                      <textarea id="inv-message" data-testid="invite-message" rows={4} maxLength={2000} value={form.message} onChange={set("message")} placeholder="Audience background, event theme, on-site or online, etc." className={areaCls} />
                    </Field>
                  </FormSection>
                  <Button type="submit" data-testid="invite-submit" disabled={busy} className="group w-full mt-8 h-12 rounded-full bg-violet-500 hover:bg-violet-400 text-white font-semibold text-base glow-violet">
                    {busy ? "Sending…" : <>Send invite <ArrowRight className="w-4 h-4 ml-1.5 transition-transform group-hover:translate-x-1" /></>}
                  </Button>
                  <p className="mt-3 text-[11px] text-zinc-500 text-center">Sankalp&apos;s team replies by email to plan the session with you.</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
      <Footer variant="adult" />
    </div>
  );
}

function TicketArt() {
  const bars = [10, 18, 26, 16, 30, 22, 34, 20, 28, 14, 24, 32, 18, 26, 12];
  return (
    <div aria-hidden className="mt-10 hidden lg:block max-w-sm rounded-2xl border border-violet-500/30 bg-gradient-to-br from-violet-500/15 via-transparent to-amber-500/5 p-5">
      <div className="flex items-center justify-between text-[10px] font-adult-mono uppercase tracking-[0.2em] text-violet-300">
        <span>Live session</span><span>90 min</span>
      </div>
      <div className="mt-3 font-adult-heading text-xl">Sankalp Sidhanth</div>
      <div className="text-xs text-zinc-400">Talk · Live Q&amp;A · Your campus or office</div>
      <div className="mt-4 pt-3 border-t border-dashed border-violet-500/30 flex items-end gap-1 h-12">
        {bars.map((h, i) => <span key={i} className="flex-1 rounded-sm bg-violet-400/60" style={{ height: `${h}px` }} />)}
      </div>
    </div>
  );
}

function InviteIntro() {
  const points = [{ Icon: Mic2, t: "A 90-minute interactive session" }, { Icon: Users, t: "Live Q&A with your audience" }, { Icon: GraduationCap, t: "For colleges, universities & workplaces" }];
  return (
    <div className="lg:col-span-5 lg:pt-6 lg:sticky lg:top-24">
      <div className="text-[11px] uppercase tracking-[0.28em] text-violet-300 font-adult-mono mb-4">Invite Sankalp Sidhanth</div>
      <h1 data-testid="invite-title" className="font-adult-heading text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.04]">
        Bring Sankalp to your <span className="font-adult-serif italic text-violet-300">campus or workplace.</span>
      </h1>
      <p className="mt-6 text-zinc-300 text-lg leading-relaxed">
        Invite Sankalp for a 90-minute live session with your students or team. Share a few details about your event and his team will get in touch to plan it with you.
      </p>
      <ul className="mt-8 space-y-3">
        {points.map(({ Icon, t }) => (
          <li key={t} className="flex items-center gap-3 text-sm text-zinc-300">
            <span className="w-9 h-9 rounded-xl bg-violet-500/10 border border-violet-500/30 flex items-center justify-center text-violet-300"><Icon className="w-4 h-4" /></span>{t}
          </li>
        ))}
      </ul>
      <TicketArt />
    </div>
  );
}

function InviteSent({ email }) {
  return (
    <div data-testid="invite-success" className="rounded-3xl border border-emerald-500/30 bg-emerald-500/5 p-8 md:p-10 animate-in fade-in zoom-in-95 duration-300">
      <CheckCircle2 className="w-10 h-10 text-emerald-400" />
      <h2 className="mt-5 font-adult-heading text-3xl font-semibold tracking-tight">Thank you, your invite is in.</h2>
      <p className="mt-3 text-zinc-300 leading-relaxed">Sankalp&apos;s team will reach out to you at <span className="text-white">{email}</span> to plan the session.</p>
      <Link to="/meet-sankalp" className="mt-6 inline-flex rounded-full border border-zinc-700 hover:border-zinc-500 px-5 py-2.5 text-sm">Back to Meet Sankalp</Link>
    </div>
  );
}
